import { z } from 'zod';
import { BenefitCategory, BenefitStatus } from 'store/benefits';
import { EmbeddedCustomCategory } from 'store/custom-categories';
import { EmployeeStatus } from 'store/employees';
import {
  benefitByEmployeeGroupFiltersSchema,
  benefitsByEmployeeFiltersSchema,
  employeesByEmployeeGroupFiltersSchema,
  employeeGroupsByBenefitFiltersSchema,
  employeeGroupsByEmployeeFiltersSchema,
  employeesByBenefitFiltersSchema,
  benefitsByEmployeePreviewFiltersSchema,
} from './assignments.schemas';

export const assignmentStatuses = ['on', 'off'] as const;
export type AssignmentStatus = (typeof assignmentStatuses)[number];

export const assignmentTypes = ['individual', 'group'] as const;
export type AssignmentType = (typeof assignmentTypes)[number];

export type AssignmentEntityRelation =
  | 'benefit/employee'
  | 'benefit/employeeGroup'
  | 'employee/employeeGroup';

export interface EmployeeByBenefit {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  assignmentStatus: AssignmentStatus;
  assignedDirectly: AssignmentStatus;
  assignedByGroup: AssignmentStatus;
  assignmentCount: number;
  customCategories: EmbeddedCustomCategory[];
}

export interface EmployeeGroupByBenefit {
  id: string;
  name: string;
  assignmentStatus: AssignmentStatus;
  assignmentCount: number;
}

export interface BenefitByEmployee {
  id: string;
  name: string;
  benefitStatus: BenefitStatus;
  assignmentStatus: AssignmentStatus;
  assignmentCount: number;
  category: BenefitCategory;
  investment: number;
}

export interface BenefitByEmployeePreview {
  id: string;
  name: string;
  status: BenefitStatus;
  assignmentType: AssignmentType;
  assignmentStatus: AssignmentStatus;
}

export interface EmployeeGroupByEmployee {
  id: string;
  name: string;
  assignmentStatus: AssignmentStatus;
  assignmentCount: number;
}

export interface BenefitByEmployeeGroup {
  id: string;
  name: string;
  benefitStatus: BenefitStatus;
  assignmentStatus: AssignmentStatus;
  assignmentCount: number;
  category: BenefitCategory;
  investment: number;
}

export interface EmployeeByEmployeeGroup {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  jobTitle: string;
  assignmentStatus: AssignmentStatus;
  assignmentCount: number;
  status: EmployeeStatus;
  customCategories: EmbeddedCustomCategory[];
}

export type EmployeesByBenefitFilters = z.infer<
  typeof employeesByBenefitFiltersSchema
>;

export type EmployeeGroupsByBenefitFilters = z.infer<
  typeof employeeGroupsByBenefitFiltersSchema
>;

export type BenefitsByEmployeeFilters = z.infer<
  typeof benefitsByEmployeeFiltersSchema
>;

export type BenefitsByEmployeePreviewFilters = z.infer<
  typeof benefitsByEmployeePreviewFiltersSchema
>;

export type EmployeeGroupsByEmployeeFilters = z.infer<
  typeof employeeGroupsByEmployeeFiltersSchema
>;

export type BenefitByEmployeeGroupFilters = z.infer<
  typeof benefitByEmployeeGroupFiltersSchema
>;

export type EmployeesByEmployeeGroupFilters = z.infer<
  typeof employeesByEmployeeGroupFiltersSchema
>;

export interface AssignmentHistoryEntry {
  id: string;
  from: string;
  to: string;
}
